import { FC, useEffect, useRef, useState } from 'react';
import { Fade, TextField } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';

import {
  emailRegex,
  inputsSetting,
  notifyMessagesQuiltUpdateCreate,
} from 'utilities';
import { AddUsersInputsModel } from '../CreateMemorialQuiltStepTwo/model';
import { AccessPermissionsTextEnum } from '../CreateMemorialQuiltStepTwo/const';
import { getProfile, inviteUserToQuilt } from '../../../api/http-client/queries/profileQueries';
import { Loader } from '../../Loader/Loader';
import { useAppDispatch } from 'hooks';
import { setIsNeedUpdateUsers } from 'store/reducers/usersSlice';
import { setOpen } from 'store/reducers/notifySlice';

import styles from '../CreateMemorialQuiltStepTwo/styles.module.scss';
import { useLocation } from 'react-router-dom';
import { AUTOSAVE_INTERVAL, STEP_TWO_SNAPSHOT_KEY } from '../constants/constants';
import { createInvite, generateMessage } from './helpers';

import { Language } from 'providers/translates/enums';
import { useTranslates } from 'providers/translates';

interface AddUsersProps {
  pageName?: string;
  id: string;
}

export const AddUsers: FC<AddUsersProps> = ({ id, pageName }) => {
  const { translates: t, selectedLang } = useTranslates();
  const invitationUI = t?.quiltBuilderStepTwo?.invitationUI;
  const noEmailProvidedPopUp = t?.quiltBuilderStepTwo?.noEmailProvidedPopUp;
  const sentInvitationPopUp = t?.quiltBuilderStepTwo?.sentInvitationPopUp;
  const location = useLocation();
  const [addUsersInputs, setAddUsersInputs] = useState<AddUsersInputsModel[]>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [initialText, setInitialText] = useState('');
  const [message, setMessage] = useState(initialText);
  const [userInvite, setUserInvite] = useState({
    email: '',
    access: AccessPermissionsTextEnum.EDIT_IMAGES_MEMORIAL,
  });
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const savedDataRef = useRef({
    addUsersInputs,
    message,
    userInvite
  });

  useEffect(() => {
    const getUser = async () => {
      const profile = await getProfile();
      if (!profile || !pageName) {
        return;
      }

      const generatedText = generateMessage(selectedLang, `${profile.firstName} ${profile.lastName}`, pageName, invitationUI?.emailEmptyStateText);

      setInitialText(generatedText);

      const savedData = localStorage.getItem(STEP_TWO_SNAPSHOT_KEY);
      if (savedData) { 
        const savedStepTwo = JSON.parse(savedData) as { 
          addUsersInputs: AddUsersInputsModel[],
          message: string,
          userInvite: {
            email: string,
            access: AccessPermissionsTextEnum,
          }
        };

        if (savedStepTwo.message) {
          if (savedStepTwo.message.startsWith(generatedText)) {
            setMessage(savedStepTwo.message);
            return;
          }
        }
      } 

      setMessage(generatedText);
      localStorage.setItem(STEP_TWO_SNAPSHOT_KEY, JSON.stringify({
        ...savedDataRef.current, 
        message: generatedText
      }));
    };

    getUser();
  }, [pageName]);

  useEffect(() => {
    if (location.pathname.includes('/update')) {
      if (intervalId.current !== null) {
        clearInterval(intervalId.current);
      }

      intervalId.current = setInterval(() => {
        localStorage.setItem(STEP_TWO_SNAPSHOT_KEY, JSON.stringify(savedDataRef.current));
      }, AUTOSAVE_INTERVAL);

      const savedData = localStorage.getItem(STEP_TWO_SNAPSHOT_KEY);
      if (savedData) { 
        const savedStepTwo = JSON.parse(savedData) as { 
          addUsersInputs: AddUsersInputsModel[],
          message: string,
          userInvite: {
            email: string,
            access: AccessPermissionsTextEnum,
          }
        };

        savedStepTwo.addUsersInputs && setAddUsersInputs(savedStepTwo.addUsersInputs);
        savedStepTwo.userInvite && setUserInvite(savedStepTwo.userInvite);

        return;
      }
    } 

    return () => {
      if (intervalId.current !== null) {
        clearInterval(intervalId.current);
      }
    }
  }, []);

  useEffect(() => {
    savedDataRef.current = {addUsersInputs, message, userInvite};
  }, [addUsersInputs, message, userInvite]);

  const handleChangeMessage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newText = event.target.value;
    if (newText.startsWith(initialText)) {
      setMessage(newText);
    }
  };

  const setUserInfo = (email: string, e: any, field: 'email' | 'access') => {
    setAddUsersInputs((state) => {
      const indexEmail = state.findIndex((item) => item.email === email);

      state[indexEmail][field] = e.target.value as never;

      return [...state];
    });
  };

  const addNewInputUser = (email?: string, access?: AccessPermissionsTextEnum) => {
    setAddUsersInputs((state) => {
      return [...state, createInvite(email, access)];
    });
    setUserInvite({ email: '', access: AccessPermissionsTextEnum.EDIT_IMAGES });
  };

  const sendMessage = () => {
    if (!message) {
      dispatch(setOpen(notifyMessagesQuiltUpdateCreate.two.noMessage));
      return;
    }

    const invitedEmails = !!userInvite.email
      ? [createInvite(userInvite.email, userInvite.access), ...addUsersInputs]
      : addUsersInputs;

    if (invitedEmails.length <= 0) {
      dispatch(setOpen(noEmailProvidedPopUp?.noEmailProvidedText ?? notifyMessagesQuiltUpdateCreate.two.noEmails));
      return;
    }

    const allInvites: Promise<void>[] = [];
    const wrongEmails: string[] = [];

    invitedEmails.forEach((addUserInput) => {
      if (emailRegex.test(addUserInput.email)) {
        allInvites.push(
          inviteUserToQuilt({
            email: addUserInput.email,
            access: AccessPermissionsTextEnum.EDIT_IMAGES_MEMORIAL,
            message: message,
            id: id || '',
          })
        );
      } else {
        wrongEmails.push(addUserInput.email);
      }
    });

    setLoading(true);

    Promise.all(allInvites)
      .then(() => {
        dispatch(setOpen(sentInvitationPopUp?.sentInvitationText ?? notifyMessagesQuiltUpdateCreate.two.success));

        if (wrongEmails.length > 0)
          dispatch(setOpen(noEmailProvidedPopUp?.noEmailProvidedText ?? notifyMessagesQuiltUpdateCreate.two.wrongEmails(wrongEmails)));
      })
      .catch((e) => {
        dispatch(setOpen(e?.errors));
      })
      .finally(() => {
        setUserInvite({ email: '', access: AccessPermissionsTextEnum.EDIT_IMAGES_MEMORIAL });
        setLoading(false);
        setAddUsersInputs([]);
        setMessage(initialText);
        dispatch(setIsNeedUpdateUsers(true));
      });
  };

  return (
    <div className={styles.addUser}>
      <div className="inputWithTitle">
        <span>
          {invitationUI?.addUsersHeading ?? 'Add Users'}
        </span>
      </div>
      {addUsersInputs.length ? (
        <div className={styles.addUser__block}>
          <div className={styles.addUser__blockInput}>
            {addUsersInputs.map((addUsersInput) => {
              return (
                <Fade
                  in={!!(typeof addUsersInput.email === 'string')}
                  timeout={1000}
                  key={addUsersInput.id}
                >
                  <div className={styles.addUser__blockInput2} key={addUsersInput.id}>
                    <div className="inputWithTitle">
                      <TextField
                        className="customInput"
                        variant="outlined"
                        placeholder={invitationUI?.emailAddressEmptyStateText ?? 'Email address'}
                        required
                        value={addUsersInput?.email || ''}
                        onChange={(e) => setUserInfo(addUsersInput?.email, e, 'email')}
                        type="email"
                        key={addUsersInput.id}
                        sx={{ minWidth: { md: '100%', lg: '550px' } }}
                      />
                    </div>
                    <div className={styles.addUser__right}>
                      <div
                        className={styles.addUser__addIcon}
                        onClick={() => {
                          setAddUsersInputs((users) =>
                            users.filter((user) => addUsersInput.id !== user.id)
                          );
                        }}
                      >
                        <DeleteOutlineOutlinedIcon></DeleteOutlineOutlinedIcon>
                      </div>
                    </div>
                  </div>
                </Fade>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className={styles.addUser__blockDisable}>
        <div className={styles.addUser__blockDisableTop}>
          <div className={styles.addUser__blockDisableTopEmailWrap}>
            <TextField
              className="customInput"
              variant="outlined"
              placeholder={invitationUI?.emailAddressEmptyStateText ?? 'Email address'}
              sx={inputsSetting.sxWidth}
              value={userInvite.email || ''}
              onChange={(e) => setUserInvite((prev) => ({ ...prev, email: e.target.value }))}
            />
          </div>
          <div className={styles.addUser__right}>
            <div
              className={styles.addUser__addIcon}
              onClick={() => addNewInputUser(userInvite.email, userInvite.access)}
            >
              <AddIcon></AddIcon>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.addUser__blockDisableBottom}>
        <TextField
          inputRef={inputRef}
          id="inputMessage"
          value={message}
          onChange={handleChangeMessage}
          className="customInput"
          variant="outlined"
          required
          sx={{
            ...inputsSetting.sxWidth,
            '& .MuiInputBase-input': {
              color: message === initialText ? 'gray' : 'black',
            },
            '& .MuiInputBase-input:focus-visible': {
              color: 'black',
            },
          }}
          multiline
          minRows={8}
          maxRows={12}
          placeholder={message}
        />
      </div>

      <div className={styles.addUser__blockDisableBottomAction}>
        <button className="customButton" onClick={sendMessage}>
          {invitationUI?.sendInviteButtonLabel ?? 'SEND INVITE'}
        </button>
      </div>

      <Loader loading={loading} />
    </div>
  );
};
